import React, { useState } from 'react'
import { FieldArray, useFormikContext } from 'formik'
import { useLingui } from '@lingui/react'
import { msg } from '@lingui/macro'

import Alert from '@mui/material/Alert'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'

import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'

import InfoSection from '@/components/ui/sections/InfoSection'
import { parentValueGenerator } from '@/utils/patient'

import { ParentDialog } from '@/features/patient/patient-profile-form/ui/ParentDialog'
import type { ParentFormData } from '@/features/patient/patient-profile-form/model/useParentForm'

/** Form values shape expected by this component (parents array) */
export type ParentsFormValues = {
  parents: Array<{
    firstname: string
    lastname: string
    email?: string
    phone?: string
    city?: string
    cp?: string
  }>
}

export type ParentsFieldArrayProps = {
  readOnly?: boolean
}

export function ParentsFieldArray({ readOnly }: ParentsFieldArrayProps) {
  const { _: i18n } = useLingui()
  const { values } = useFormikContext<ParentsFormValues>()

  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>()

  const openAdd = () => setAddDialogOpen(true)
  const closeAdd = () => setAddDialogOpen(false)
  const openEdit = (index: number) => {
    setSelectedIndex(index)
    setEditDialogOpen(true)
  }
  const closeEdit = () => {
    setEditDialogOpen(false)
    setSelectedIndex(undefined)
  }

  const toParentFormData = (p: ParentsFormValues['parents'][number]): ParentFormData => ({
    firstname: p.firstname,
    lastname: p.lastname,
    email: p.email ?? '',
    phone: p.phone ?? '',
    city: p.city ?? '',
    cp: typeof p.cp === 'string' ? p.cp : String(p.cp ?? ''),
  })

  return (
    <FieldArray name="parents">
      {({ push, remove, replace }) => (
        <InfoSection
          action={
            !readOnly && (
              <Tooltip title={i18n(msg`Add parent`)}>
                <IconButton onClick={openAdd} size="small">
                  <AddRoundedIcon />
                </IconButton>
              </Tooltip>
            )
          }
          label={i18n(msg`Parents`)}
        >
          {!values.parents.length && readOnly ? (
            <Alert icon={false} severity="info" variant="outlined" sx={{ mt: '10px' }}>
              {i18n(msg`No parents registered`)}
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ mt: '16px' }}>
              <Table>
                <TableBody>
                  {values.parents.map((parent, index) => (
                    <TableRow key={index}>
                      <TableCell component="th" scope="row">
                        {`${parent.firstname} ${parent.lastname}`}
                      </TableCell>
                      <TableCell align="right">
                        <Stack display="inline-flex" direction="row" spacing={1}>
                          <Tooltip title={i18n(msg`View`)}>
                            <IconButton onClick={() => openEdit(index)} size="small">
                              <VisibilityRoundedIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                          {!readOnly && (
                            <Tooltip title={i18n(msg`Delete`)}>
                              <IconButton onClick={() => remove(index)} size="small">
                                <DeleteRoundedIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      {editDialogOpen && selectedIndex === index && (
                        <ParentDialog
                          data={toParentFormData(parent)}
                          onClose={closeEdit}
                          onSubmit={(data) => {
                            replace(index, data)
                            closeEdit()
                          }}
                          open
                          readOnly={readOnly}
                        />
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {addDialogOpen && (
            <ParentDialog
              data={toParentFormData(parentValueGenerator())}
              onClose={closeAdd}
              onSubmit={(data) => {
                push(data)
                closeAdd()
              }}
              open
            />
          )}
        </InfoSection>
      )}
    </FieldArray>
  )
}

export default ParentsFieldArray
